import type { Book } from '@booknest/shared';

import { BookcaseUnit, type BookcaseUnitData } from './BookcaseUnit.js';
import type { Density } from './spine-utils.js';

export type BookcaseView = 'flat' | '3d';

interface BookcaseProps {
  units: ReadonlyArray<BookcaseUnitData>;
  view?: BookcaseView;
  density?: Density;
  openingBookId?: string | null;
  onBookClick?: (book: Book) => void;
  /**
   * Nombre maximum de colonnes même sur très grand écran. Sert à empêcher
   * les étagères de devenir trop larges (1 colonne sur grand écran = très
   * laid en cas de tranches étirées). Défaut : pas de plafond.
   */
  maxCols?: 1 | 2 | 3 | 4;
}

/**
 * Bookcase — affiche N meubles en grille responsive.
 *
 * Grille adaptative (`.bookcase-grid.cols-N`) :
 *  - 1 col par défaut
 *  - 2 cols ≥ 1280 (xl)
 *  - 3 cols ≥ 1536 (2xl)
 *  - 4 cols ≥ 1920
 *
 * Toutes les units partagent la même hauteur grâce à `align-items: stretch`
 * (default grid) + `bookcase-unit { flex: 1 }` côté CSS.
 *
 * Chaque unit pagine ses livres pour ne JAMAIS produire de scroll horizontal.
 */
export function Bookcase({
  units,
  view = 'flat',
  density = 'comfy',
  openingBookId = null,
  onBookClick,
  maxCols,
}: BookcaseProps): JSX.Element {
  // Auto-derivation : 1→full width, 2→cols-2, 3→cols-2+cols-3, 4+→cols-2+3+4.
  // `maxCols` plafonne la liste produite : passer maxCols=2 garde au plus
  // 2 colonnes même sur écran 4k.
  const desired = Math.min(units.length, maxCols ?? units.length);
  const colsClass =
    desired >= 4
      ? 'cols-2 cols-3 cols-4'
      : desired === 3
        ? 'cols-2 cols-3'
        : desired === 2
          ? 'cols-2'
          : '';

  // Alignement vertical des meubles : on calcule la longueur du meuble qui a
  // le plus de livres et on la diffuse à tous les autres pour qu'ils paddent
  // leurs rangées du bas et que toutes les bookcases d'une même grille aient
  // la même hauteur visible.
  const matchToBookCount = units.reduce(
    (max, u) => Math.max(max, u.books.length),
    0,
  );

  return (
    <div className={`bookcase-grid ${colsClass}`.trim()}>
      {units.map(({ key, ...unit }) => (
        <BookcaseUnit
          key={key}
          {...unit}
          view={view}
          density={density}
          matchToBookCount={matchToBookCount}
          openingBookId={openingBookId}
          onBookClick={onBookClick}
        />
      ))}
    </div>
  );
}

export type { BookcaseUnitData };
